import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { AppInsightsService, ToastMessageService } from '@k12/app-services';
import { AdminUserType, PartnerTypeIdToUserType, SchoolRecordVm, HouseholdRecordVm, SeverityLevel, ToastMessageType, ProviderData } from '@k12/models';
import { Observable, of } from 'rxjs';
import { ProviderRecordService } from './provider-record.service';
import { AdminHouseholdRecordService } from './admin-household-record/admin-household-record.service';
import { AdminSchoolRecordService } from './admin-school-record/admin-school-record.service';
import { environment } from '../../environments/environment';

export interface UserRecordResolverData {
  userType: AdminUserType;
  schoolDetails: SchoolRecordVm | null;
  householdDetails: HouseholdRecordVm | null;
  providerDetails?: ProviderData | null;
}

export const userRecordResolver: ResolveFn<UserRecordResolverData> = (route) => {
  const adminSchoolRecordService = inject(AdminSchoolRecordService);
  const adminHouseholdRecordService = inject(AdminHouseholdRecordService);
  const appInsightsService = inject(AppInsightsService);
  const providerRecordService = inject(ProviderRecordService);
  const toastMessageService = inject(ToastMessageService);

  const programPartnerId: string = route.params['id'];
  const partnerTypeId: string = route.queryParams['partnerTypeId'];

  // Return default Provider type if required route parameters are missing.
  if (!programPartnerId || !partnerTypeId) {
    return of({
      userType: AdminUserType.Provider,
      schoolDetails: null,
      householdDetails: null,
      providerDetails: null
    });
  }

  const userType = PartnerTypeIdToUserType[Number.parseInt(partnerTypeId)] ?? AdminUserType.Provider;

  if (userType === AdminUserType.Schools) {
    return new Observable<UserRecordResolverData>(observer => {
      adminSchoolRecordService.getSchoolDetails(programPartnerId).subscribe({
        next: (schoolDetails) => {
          observer.next({
            userType,
            schoolDetails,
            householdDetails: null,
            providerDetails: null
          });
          observer.complete();
        },
        error: (error) => {
          console.error('Error loading school details:', error);
          appInsightsService.log(
            new Error(`Admin: ${environment.name}: Failed to load school details - ${error?.message || error}`),
            SeverityLevel.error
          );
          toastMessageService.showToast(ToastMessageType.Error, 'Failed to load school details.');
          observer.next({
            userType,
            schoolDetails: null,
            householdDetails: null,
            providerDetails: null
          });
          observer.complete();
        }
      });
    });
  } else if (userType === AdminUserType.Households) {
    return new Observable<UserRecordResolverData>((observer) => {
      adminHouseholdRecordService.getHouseholdDetails(programPartnerId).subscribe({
        next: (householdDetails) => {
          observer.next({
            userType,
            schoolDetails: null,
            householdDetails,
            providerDetails: null
          });
          observer.complete();
        },
        error: (error) => {
          console.error('Error loading household details:', error);
          appInsightsService.log(
            new Error(`Admin: ${environment.name}: Failed to load household details - ${error?.message || error}`),
            SeverityLevel.error
          );
          toastMessageService.showToast(ToastMessageType.Error, 'Failed to load household details.');
          observer.next({
            userType,
            schoolDetails: null,
            householdDetails: null,
            providerDetails: null
          });
          observer.complete();
        }
      });
    });
  } else if (userType === AdminUserType.Provider) {
    return new Observable<UserRecordResolverData>((observer) => {
      providerRecordService.getProviderDetails(programPartnerId).subscribe({
        next: (providerDetails) => {
          observer.next({
            userType,
            schoolDetails: null,
            householdDetails: null,
            providerDetails
          });
          observer.complete();
        },
        error: (error) => {
          console.error('Error loading provider details:', error);
          appInsightsService.log(
            new Error(`Admin: ${environment.name}: Failed to load provider details - ${error?.message || error}`),
            SeverityLevel.error
          );
          toastMessageService.showToast(ToastMessageType.Error, 'Failed to load provider details.');
          observer.next({
            userType,
            schoolDetails: null,
            householdDetails: null,
            providerDetails: null
          });
          observer.complete();
        }
      });
    });
  }

  return of({
    userType,
    schoolDetails: null,
    householdDetails: null,
    providerDetails: null
  });
};
